import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { patchCurrentUser } from "../redux/userRelated/userSlice";
import { getStaffRoleFromUser, isHomeroomTeacherRole, normalizeStaffRole } from "../utils/staffRoles";

function normalizeHomeroomList(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map((c) => ({
        ...c,
        _id: c._id || c.id,
        id: c.id || c._id,
        sclassName: c.sclassName || c.name || "—",
    }));
}

function homeroomIdsKey(list) {
    return normalizeHomeroomList(list)
        .map((c) => c.id)
        .filter(Boolean)
        .sort()
        .join(",");
}

/**
 * Classes where this teacher is the assigned homeroom teacher (classTeacherId).
 */
export function useFormTeacherHomeroom() {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const teacherId = currentUser?._id || currentUser?.id;

    const [homeroomClasses, setHomeroomClasses] = useState(() =>
        normalizeHomeroomList(currentUser?.classTeacherOf),
    );
    const [loading, setLoading] = useState(false);
    const fetchGenRef = useRef(0);

    const refresh = useCallback(async () => {
        if (!teacherId) {
            setHomeroomClasses([]);
            return [];
        }
        const gen = ++fetchGenRef.current;
        setLoading(true);
        try {
            const [classesRes, teacherRes] = await Promise.all([
                axios.get(`${getApiBaseUrl()}/Enrollment/teacher/${teacherId}/classes`),
                axios.get(`${getApiBaseUrl()}/Teacher/${teacherId}`).catch(() => ({ data: null })),
            ]);
            if (gen !== fetchGenRef.current) return [];

            const fromEnrollment = normalizeHomeroomList(classesRes.data);
            const fromTeacher =
                teacherRes.data && !teacherRes.data?.message
                    ? normalizeHomeroomList(teacherRes.data.classTeacherOf)
                    : [];
            const list = fromEnrollment.length > 0 ? fromEnrollment : fromTeacher;
            setHomeroomClasses(list);

            const patch = {};
            const nextHomeroomKey = homeroomIdsKey(list);
            const prevHomeroomKey = homeroomIdsKey(currentUser?.classTeacherOf);
            if (nextHomeroomKey !== prevHomeroomKey) {
                patch.classTeacherOf = list;
            }

            if (teacherRes.data && !teacherRes.data?.message && teacherRes.data.staffRole != null) {
                const nextRole = normalizeStaffRole(teacherRes.data.staffRole);
                const prevRole = getStaffRoleFromUser(currentUser);
                if (nextRole !== prevRole) {
                    patch.staffRole = nextRole;
                }
            }

            if (Object.keys(patch).length > 0) {
                dispatch(patchCurrentUser(patch));
            }

            return list;
        } catch {
            if (gen === fetchGenRef.current) {
                setHomeroomClasses(normalizeHomeroomList(currentUser?.classTeacherOf));
            }
            return normalizeHomeroomList(currentUser?.classTeacherOf);
        } finally {
            if (gen === fetchGenRef.current) {
                setLoading(false);
            }
        }
    }, [teacherId, currentUser, dispatch]);

    useEffect(() => {
        refresh();
    }, [teacherId]); // eslint-disable-line react-hooks/exhaustive-deps -- load once per login id

    const primaryHomeroom = homeroomClasses[0] || null;
    const homeroomClassId = primaryHomeroom?._id || primaryHomeroom?.id || null;
    const homeroomClassName = primaryHomeroom?.sclassName || null;
    const homeroomLabel =
        homeroomClasses.length === 0
            ? ""
            : homeroomClasses.length === 1
              ? homeroomClassName
              : `${homeroomClassName} +${homeroomClasses.length - 1}`;

    const roleIsHomeroom = isHomeroomTeacherRole(getStaffRoleFromUser(currentUser));
    const hasHomeroomClass = homeroomClasses.length > 0;
    const showMyClass = hasHomeroomClass || roleIsHomeroom;
    const needsHomeroomSetup = !hasHomeroomClass;

    return {
        homeroomClasses,
        homeroomClassId,
        homeroomClassName,
        homeroomLabel,
        roleIsHomeroom,
        hasHomeroomClass,
        showMyClass,
        needsHomeroomSetup,
        canEnrollStudents: hasHomeroomClass,
        isHomeroomTeacher: hasHomeroomClass,
        isFormTeacher: hasHomeroomClass,
        roleIsFormTeacher: roleIsHomeroom,
        loading,
        refresh,
    };
}
